import { isPlatformBrowser, NgClass } from '@angular/common';
import { afterNextRender,
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  InjectionToken,
  OnDestroy,
  OnInit,
  PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil }     from 'rxjs';
import { SEOService }             from 'src/app/core/seo/seo.service';
import { PageTypeService } from 'src/app/shared/services/page-type.service';
import { SubscribeSystemStringService } from 'src/app/shared/services/subscribe-system-string.service';
import { RouteQuery, RouteService, RouteStore } from 'src/app/store';
import { AppStore } from 'src/app/store/app.store';
import { PushNotificationService } from '../../services/push-notification.service';
import { ContentComponent } from '../content/content.component';
import { FooterComponent } from '../footer/footer.component';
import { HeaderComponent } from '../header/header.component';
import { NewsletterMenuComponent } from '../newsletter-menu/newsletter-menu.component';

@Component({
  selector: 'app-page-content',
  templateUrl: './page-content.component.html',
  styleUrls: ['./page-content.component.scss'],
  providers: [RouteQuery, RouteStore, RouteService],
  standalone: true,
  imports: [
    HeaderComponent,
    NgClass,
    ContentComponent,
    NewsletterMenuComponent,
    FooterComponent,
  ],
})
export class PageContentComponent implements OnInit, OnDestroy, AfterViewInit {
  pageContent!: Page;
  hostUrl!: string;
  pageType: boolean;
  protected unsubscribe = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: InjectionToken<object>,
    private activatedRoute: ActivatedRoute,
    private seoSvc: SEOService,
    private subscribeService: SubscribeSystemStringService,
    private pageTypeService: PageTypeService,
    private pushNotificationService: PushNotificationService,
    private appStore: AppStore,
    private el: ElementRef,
  ) {
    afterNextRender(() => {
      this.hostUrl = document.location.hostname;
    });

    if (activatedRoute.data != null) {
      this.activatedRoute.data.subscribe((data) => {
        this.pageContent = data['data'] as Page;
        this.subscribeService.subscribeFormDetails(
          this.pageContent.SystemStrings,
        );
        this.seoService();

        this.appStore.appName = this.pageContent.AppName ?? '';
        this.appStore.showLogonButton = this.pageContent.ShowLogonButton;
        this.appStore.systemStrings = this.pageContent.SystemStrings as any;

        if (isPlatformBrowser(this.platformId)) {
          setTimeout(() => {
            this.scrollToFragment();
          });
        }
      });
    }
    this.pageType = this.calcPageType();
    this.pageTypeService.setPageType(this.pageType);
    this.pageType && (this.pageContent.CustomCSS += ' container');
  }

  /**
   * True if the page doesnt not contain container-fluid
   */
  calcPageType(): boolean {
    return !this.pageContent.CustomCSS?.includes('container-fluid');
  }

  seoService() {
    this.seoSvc.updateTitle(this.pageContent?.Title ?? '');
    this.seoSvc.updateIcon(this.pageContent?.PageConfiguration?.Icon ?? '');
    this.seoSvc.updateMetaDescription(this.pageContent);
    this.seoSvc.updateMetaImage(this.pageContent);
    this.seoSvc.updateMetaAuthors(this.pageContent.MetaAuthors);
    this.seoSvc.updateMetaKeywords(this.pageContent.MetaKeywords ?? '');
    this.seoSvc.updateMetaCanonical(this.pageContent);
    this.seoSvc.updateDtxDynamicStyling(this.pageContent);
    this.seoSvc.updateDtxDynamicColours(this.pageContent);
    this.seoSvc.updateDtxDynamicScripts(this.pageContent);
  }

  isNewsletter(): boolean {
    return (
      this.pageContent.PageContent.length > 0 &&
      this.pageContent.PageType.toUpperCase() === 'NEWSLETTER'
    );
  }

  isLeftMenuNewsletter(): boolean {
    return this.isNewsletter() && this.pageContent.MenuLocation == 'Left';
  }

  isTopMenuNewsletter(): boolean {
    return this.isNewsletter() && this.pageContent.MenuLocation == 'Top';
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.pushNotificationService.unsubscribe();
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.activatedRoute.fragment
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(() => {
          this.scrollToFragment();
        });
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.scrollToFragment();
      });
    }
  }

  scrollToFragment() {
    const fragment: string | null = this.activatedRoute.snapshot.fragment;
    if (fragment === null || fragment === '') {
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
      return;
    }
    const element: HTMLDivElement | null = this.el.nativeElement.querySelector(
      '#' + fragment,
    );
    if (element !== null) {
      const headerOffset = 100;
      const offsetPosition = element.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'auto',
      });
    }
  }
}
